import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Waitlist = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: 'candidate'
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be replaced with actual API call
    setSubmitted(true);
    toast({
      title: 'Welcome to the Waitlist!',
      description: 'We\'ll notify you as soon as we launch.',
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-[#42b1c2]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              You're on the list!
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Thank you for joining the Luma Med Global waitlist. We'll send you an email as soon as we launch.
            </p>
            <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                What happens next?
              </h2>
              <ul className="text-left space-y-3 max-w-md mx-auto">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">You'll receive a confirmation email shortly</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">We'll keep you updated on our launch progress</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Early access to platform features before public launch</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Special promotional pricing for early adopters</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Join the Waitlist
            </h1>
            <p className="text-xl text-gray-600">
              Be among the first to experience AI-powered medical recruitment. Early members get exclusive benefits.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Early Access Benefits
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Priority access when we launch</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">50% discount on first 3 months (employers)</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Free premium features for 6 months (candidates)</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Direct input into platform features</span>
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="mb-3 block">I am a:</Label>
                <RadioGroup
                  value={formData.userType}
                  onValueChange={(value) => setFormData({ ...formData, userType: value })}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 border-2 border-gray-200 rounded-lg p-4 hover:border-[#42b1c2] transition-colors cursor-pointer">
                    <RadioGroupItem value="candidate" id="candidate" />
                    <Label htmlFor="candidate" className="cursor-pointer flex-1">
                      <div className="font-semibold text-gray-900">Medical Professional</div>
                      <div className="text-sm text-gray-600">Looking for opportunities in Saudi Arabia</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 border-2 border-gray-200 rounded-lg p-4 hover:border-[#42b1c2] transition-colors cursor-pointer">
                    <RadioGroupItem value="employer" id="employer" />
                    <Label htmlFor="employer" className="cursor-pointer flex-1">
                      <div className="font-semibold text-gray-900">Healthcare Employer</div>
                      <div className="text-sm text-gray-600">Recruiting medical professionals</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button type="submit" className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white py-6 text-lg">
                Join the Waitlist
              </Button>

              <p className="text-center text-sm text-gray-500">
                By joining, you agree to receive updates from Luma Med Global. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Waitlist;